import { useState, useCallback, useMemo } from 'react';
import {
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  QrCode as QrIcon,
} from '@mui/icons-material';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { roundMoney } from '../../../utils/money';
import {
  clampUniformDiscountValue,
  getMaxUniformDiscountAmount,
  hasPositiveCatalogSubtotal,
  isLineDiscountInvalid,
  isUniformAmountDiscountInvalid,
  projectUniformAdjustedPrices,
} from '../../../utils/sales/line-pricing.utils';
import { formatCurrency } from '../../../utils/formatters/money';
import { PaymentMethod } from '../../../api/models/PaymentMethod';
import { PAYMENT_METHODS } from '../../../utils/constants/paymentMethods';
import { useOtherPaymentMethods } from '../../../hooks/useOtherPaymentMethods';
import { SalesStep2FlowPhase } from '../../../types/sales.types';

const PAYMENT_METHOD_OPTIONS = [
  { id: 'CASH', name: 'Efectivo', icon: MoneyIcon, color: 'success.main' },
  { id: 'CARD', name: 'Tarjeta', icon: CardIcon, color: 'primary.main' },
  { id: 'QR', name: 'QR', icon: QrIcon, color: 'secondary.main' },
];

export type { SalesStep2FlowPhase };

interface PricingConfigDraft {
  markupEnabled: boolean;
  allowIndividualMarkups: boolean;
  markupType: 'PERCENTAGE' | 'AMOUNT';
  markupValue: number;
  discountEnabled: boolean;
  allowIndividualDiscounts: boolean;
  discountType: 'PERCENTAGE' | 'AMOUNT';
  discountValue: number;
}

const INITIAL_PRICING_DRAFT: PricingConfigDraft = {
  markupEnabled: false,
  allowIndividualMarkups: false,
  markupType: 'AMOUNT',
  markupValue: 0,
  discountEnabled: false,
  allowIndividualDiscounts: false,
  discountType: 'AMOUNT',
  discountValue: 0,
};

function getProjectedAdjustedPrices(
  products: Array<{ originalPrice: number }>,
  draft: PricingConfigDraft,
): Array<{ adjustedPrice: number }> {
  if (!draft.markupEnabled || draft.allowIndividualMarkups) {
    return products.map((product) => ({ adjustedPrice: roundMoney(product.originalPrice) }));
  }

  return projectUniformAdjustedPrices(products, {
    markupType: draft.markupType,
    markupValue: draft.markupValue,
  });
}

export const useSalesStep2 = () => {
  const {
    state,
    orderSummary,
    updateDiscountConfig,
    updateMarkupConfig,
    updatePaymentConfig,
    updateProductMarkup,
    updateProductDiscount,
    applyUniformMarkupToAllProducts,
    applyUniformDiscountToAllProducts,
    resetAllLinePricing,
    commitCommercialPricingConfig,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
    clearPayments,
    addPayment,
    removePayment,
    updatePayment,
    addOrderCharge,
    updateOrderCharge,
    removeOrderCharge,
    chargeCategories,
    canProceedToStep3,
    nextStep,
    previousStep,
  } = useSalesModal();

  const flowPhase = state.step2FlowPhase;
  const [pricingDraft, setPricingDraft] = useState<PricingConfigDraft>(INITIAL_PRICING_DRAFT);
  const { otherPaymentMethods, getPaymentMethodName } = useOtherPaymentMethods();
  const [showOtherMethodsDialog, setShowOtherMethodsDialog] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [showPaymentAmountDialog, setShowPaymentAmountDialog] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<string | null>(null);

  const updatePricingDraft = useCallback((partial: Partial<PricingConfigDraft>) => {
    setPricingDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetPricingDraft = useCallback(() => {
    setPricingDraft(INITIAL_PRICING_DRAFT);
  }, []);

  const closePaymentUi = useCallback(() => {
    setShowPaymentAmountDialog(false);
    setShowOtherMethodsDialog(false);
    setPendingPaymentMethod(null);
    setEditingPaymentId(null);
  }, []);

  const projectedAdjustedProducts = useMemo(
    () => getProjectedAdjustedPrices(state.selectedProducts, pricingDraft),
    [pricingDraft, state.selectedProducts],
  );

  const maxUniformDiscountAmount = useMemo(() => {
    if (flowPhase === 1) {
      return getMaxUniformDiscountAmount(projectedAdjustedProducts);
    }
    return getMaxUniformDiscountAmount(state.selectedProducts);
  }, [flowPhase, projectedAdjustedProducts, state.selectedProducts]);

  const orderMarkupHasNoCatalogBase = useMemo(
    () =>
      pricingDraft.markupEnabled &&
      !pricingDraft.allowIndividualMarkups &&
      !hasPositiveCatalogSubtotal(state.selectedProducts),
    [pricingDraft.allowIndividualMarkups, pricingDraft.markupEnabled, state.selectedProducts],
  );

  const canConfirmConfigSection = useMemo(() => {
    if (state.selectedProducts.length === 0) {
      return false;
    }

    if (!pricingDraft.discountEnabled || pricingDraft.allowIndividualDiscounts) {
      return true;
    }

    if (pricingDraft.discountType === 'PERCENTAGE') {
      return pricingDraft.discountValue <= 99;
    }

    return !isUniformAmountDiscountInvalid(
      flowPhase === 1 ? projectedAdjustedProducts : state.selectedProducts,
      pricingDraft.discountValue,
    );
  }, [flowPhase, pricingDraft, projectedAdjustedProducts, state.selectedProducts]);

  const hasCommercialConfig = useMemo(
    () =>
      flowPhase === 1
        ? pricingDraft.markupEnabled || pricingDraft.discountEnabled
        : state.markupConfig.enabled || state.discountConfig.enabled,
    [
      flowPhase,
      pricingDraft.discountEnabled,
      pricingDraft.markupEnabled,
      state.discountConfig.enabled,
      state.markupConfig.enabled,
    ],
  );

  const hasInvalidLinePricing = useMemo(() => {
    if (flowPhase < 2 || !hasCommercialConfig) {
      return false;
    }

    const invalidMarkup = state.selectedProducts.some(
      (product) => product.adjustedPrice < product.originalPrice,
    );

    if (invalidMarkup) {
      return true;
    }

    if (!state.discountConfig.enabled) {
      return false;
    }

    if (
      !state.discountConfig.allowIndividualDiscounts &&
      state.discountConfig.orderDiscountType === 'AMOUNT'
    ) {
      return isUniformAmountDiscountInvalid(
        state.selectedProducts,
        state.discountConfig.orderDiscountValue,
      );
    }

    return state.selectedProducts.some((product) =>
      isLineDiscountInvalid(product.adjustedPrice, product.discountType, product.discountValue),
    );
  }, [flowPhase, hasCommercialConfig, state.discountConfig, state.selectedProducts]);

  const canConfirmProductsSection = useMemo(() => {
    return state.selectedProducts.length > 0 && !hasInvalidLinePricing;
  }, [state.selectedProducts.length, hasInvalidLinePricing]);

  const commitPricingConfig = useCallback(() => {
    if (!canConfirmConfigSection) return;
    closePaymentUi();
    commitCommercialPricingConfig(pricingDraft);
  }, [canConfirmConfigSection, closePaymentUi, commitCommercialPricingConfig, pricingDraft]);

  const confirmProductsSection = useCallback(() => {
    if (!canConfirmProductsSection) return;
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(3);
  }, [
    canConfirmProductsSection,
    clearPayments,
    closePaymentUi,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
  ]);

  const confirmChargesSection = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(4);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const confirmPaymentConfigSection = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(5);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockConfigForEdit = useCallback(() => {
    resetAllLinePricing();
    resetPricingDraft();
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(1);
  }, [
    clearPayments,
    closePaymentUi,
    resetAllLinePricing,
    resetPricingDraft,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
  ]);

  const unlockProductsForEdit = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(2);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockChargesForEdit = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(3);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockPaymentConfigForEdit = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(4);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockPaymentMethodsForEdit = useCallback(() => {
    closePaymentUi();
    setStep2PaymentsLocked(false);
  }, [closePaymentUi, setStep2PaymentsLocked]);

  const handleMarkupToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updatePricingDraft({
          markupEnabled: false,
          allowIndividualMarkups: false,
          markupType: 'AMOUNT',
          markupValue: 0,
        });
        return;
      }
      updatePricingDraft({ markupEnabled: true });
    },
    [updatePricingDraft],
  );

  const handleIndividualMarkupToggle = useCallback(
    (enabled: boolean) => {
      updatePricingDraft({ allowIndividualMarkups: enabled });
    },
    [updatePricingDraft],
  );

  const handleMarkupTypeChange = useCallback(
    (type: 'PERCENTAGE' | 'AMOUNT') => {
      updatePricingDraft({ markupType: type, markupValue: 0 });
    },
    [updatePricingDraft],
  );

  const handleMarkupValueChange = useCallback(
    (value: number) => {
      updatePricingDraft({ markupValue: roundMoney(Math.max(0, value)) });
    },
    [updatePricingDraft],
  );

  const handleProductMarkupChange = useCallback(
    (productId: number, type: 'PERCENTAGE' | 'AMOUNT', value: number) => {
      updateProductMarkup(productId, type, roundMoney(Math.max(0, value)));
    },
    [updateProductMarkup],
  );

  const handleDiscountToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updatePricingDraft({
          discountEnabled: false,
          allowIndividualDiscounts: false,
          discountType: 'AMOUNT',
          discountValue: 0,
        });
        return;
      }
      updatePricingDraft({ discountEnabled: true });
    },
    [updatePricingDraft],
  );

  const handleIndividualDiscountToggle = useCallback(
    (enabled: boolean) => {
      updatePricingDraft({ allowIndividualDiscounts: enabled });
    },
    [updatePricingDraft],
  );

  const handleDiscountTypeChange = useCallback(
    (type: 'PERCENTAGE' | 'AMOUNT') => {
      updatePricingDraft({ discountType: type, discountValue: 0 });
    },
    [updatePricingDraft],
  );

  const handleDiscountValueChange = useCallback(
    (value: number) => {
      const validatedValue = clampUniformDiscountValue(
        pricingDraft.discountType,
        value,
        projectedAdjustedProducts,
      );
      updatePricingDraft({ discountValue: validatedValue });
    },
    [pricingDraft.discountType, projectedAdjustedProducts, updatePricingDraft],
  );

  const handleProductDiscountChange = useCallback(
    (productId: number, type: 'PERCENTAGE' | 'AMOUNT' | 'NONE', value: number) => {
      updateProductDiscount(productId, type, value);
    },
    [updateProductDiscount],
  );

  const paymentsLocked = state.step2PaymentsLocked;
  const chargesSectionVisible = flowPhase >= 3;
  const chargesEditable = flowPhase === 3 && !paymentsLocked;
  const paymentConfigVisible = flowPhase >= 4;
  const paymentConfigEditable = flowPhase === 4 && !paymentsLocked;
  const paymentMethodsVisible = flowPhase >= 5;
  const paymentMethodsEditable = flowPhase === 5 && !paymentsLocked;
  /** @deprecated Prefer paymentMethodsEditable; kept for gradual call-site updates */
  const paymentsSectionVisible = paymentMethodsVisible;
  const paymentsEditable = paymentMethodsEditable;

  const handlePaymentMethodClick = useCallback(
    (method: string) => {
      if (!paymentsEditable) return;
      const isSinglePaymentMode = !state.paymentConfig.allowMultiplePayments;
      const isPartialOff = !state.paymentConfig.allowPartialPayment;
      if (isSinglePaymentMode && state.paymentConfig.payments.length >= 1) {
        return;
      }

      if (isSinglePaymentMode && isPartialOff) {
        if (method !== PAYMENT_METHODS.CASH) {
          const totalToCharge = orderSummary?.grandTotal ?? 0;
          addPayment({
            method,
            amount: roundMoney(totalToCharge),
          });
          return;
        }
      }

      setPendingPaymentMethod(method);
      setShowPaymentAmountDialog(true);
    },
    [
      addPayment,
      paymentsEditable,
      orderSummary,
      state.paymentConfig.allowMultiplePayments,
      state.paymentConfig.allowPartialPayment,
      state.paymentConfig.payments.length,
    ],
  );

  const handleOtherMethodSelect = useCallback(
    (method: PaymentMethod) => {
      if (!paymentsEditable) return;
      const isSinglePaymentMode = !state.paymentConfig.allowMultiplePayments;
      const isPartialOff = !state.paymentConfig.allowPartialPayment;
      if (isSinglePaymentMode && state.paymentConfig.payments.length >= 1) {
        return;
      }

      if (isSinglePaymentMode && isPartialOff) {
        const totalToCharge = orderSummary?.grandTotal ?? 0;
        addPayment({
          method: method.code,
          amount: roundMoney(totalToCharge),
        });
        setShowOtherMethodsDialog(false);
        return;
      }

      setPendingPaymentMethod(method.code);
      setShowOtherMethodsDialog(false);
      setShowPaymentAmountDialog(true);
    },
    [
      addPayment,
      paymentsEditable,
      orderSummary,
      state.paymentConfig.allowMultiplePayments,
      state.paymentConfig.allowPartialPayment,
      state.paymentConfig.payments.length,
    ],
  );

  const handleOpenOtherMethods = useCallback(() => {
    if (!paymentsEditable) return;
    if (otherPaymentMethods.length > 0) {
      setShowOtherMethodsDialog(true);
    }
  }, [paymentsEditable, otherPaymentMethods.length]);

  const handleCloseOtherMethods = useCallback(() => {
    setShowOtherMethodsDialog(false);
  }, []);

  const handleAddPayment = useCallback(
    (
      method: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      const remainingBalance =
        orderSummary.grandTotal -
        state.paymentConfig.payments.reduce((sum, p) => sum + p.amount, 0);

      const paymentAmount = amount > 0 ? amount : Math.max(0, remainingBalance);

      addPayment({
        method,
        amount: roundMoney(paymentAmount),
        cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
        paymentNotes: notes,
        bankReference,
        qrTransactionId,
      });

      setShowPaymentAmountDialog(false);
      setPendingPaymentMethod(null);
    },
    [addPayment, orderSummary.grandTotal, state.paymentConfig.payments],
  );

  const handleRemovePayment = useCallback(
    (paymentId: string) => {
      if (!paymentsEditable) return;
      removePayment(paymentId);
    },
    [paymentsEditable, removePayment],
  );

  const handleUpdatePaymentAmount = useCallback(
    (
      paymentId: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      const trimmedNotes = notes?.trim();
      updatePayment(paymentId, {
        amount: roundMoney(amount),
        cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
        paymentNotes: trimmedNotes != null && trimmedNotes !== '' ? trimmedNotes : undefined,
        bankReference,
        qrTransactionId,
      });
    },
    [updatePayment],
  );

  const handleEditPayment = useCallback(
    (paymentId: string) => {
      if (!paymentsEditable) return;
      setEditingPaymentId(paymentId);
      setShowPaymentAmountDialog(true);
    },
    [paymentsEditable],
  );

  const handleClosePaymentAmountDialog = useCallback(() => {
    setShowPaymentAmountDialog(false);
    setPendingPaymentMethod(null);
    setEditingPaymentId(null);
  }, []);

  const handlePartialPaymentToggle = useCallback(
    (enabled: boolean) => {
      updatePaymentConfig({ allowPartialPayment: enabled });
    },
    [updatePaymentConfig],
  );

  const handleMultiplePaymentsToggle = useCallback(
    (enabled: boolean) => {
      updatePaymentConfig({
        allowMultiplePayments: enabled,
        payments: enabled ? state.paymentConfig.payments : state.paymentConfig.payments.slice(0, 1),
      });
    },
    [state.paymentConfig.payments, updatePaymentConfig],
  );

  const formatAdjustmentDisplay = useCallback(
    (adjustmentType: string, adjustmentValue: number, basePrice: number) => {
      if (adjustmentType === 'PERCENTAGE') {
        const equivalentAmount = roundMoney((basePrice * adjustmentValue) / 100);
        return `${adjustmentValue}% (${formatCurrency(equivalentAmount)})`;
      }
      return formatCurrency(adjustmentValue);
    },
    [],
  );

  const getTotalPayments = useCallback(() => {
    return state.paymentConfig.payments.reduce((sum, p) => sum + p.amount, 0);
  }, [state.paymentConfig.payments]);

  const getRemainingBalance = useCallback(() => {
    return Math.max(0, orderSummary.grandTotal - getTotalPayments());
  }, [orderSummary.grandTotal, getTotalPayments]);

  const configEditable = flowPhase === 1;
  const productsEditable = flowPhase === 2;
  const productsSectionActive = hasCommercialConfig && flowPhase >= 2;
  const canProceed = canProceedToStep3 && flowPhase === 5;

  const handleNextStep = useCallback(() => {
    if (!canProceed) return;
    closePaymentUi();
    setStep2PaymentsLocked(true);
    nextStep();
  }, [canProceed, closePaymentUi, nextStep, setStep2PaymentsLocked]);

  const committedConfigDraft = useMemo((): PricingConfigDraft => {
    if (flowPhase === 1) {
      return pricingDraft;
    }

    const hasMarkup = state.selectedProducts.some(
      (product) => product.markupType !== 'NONE' && product.markupValue > 0,
    );

    return {
      markupEnabled: state.markupConfig.enabled || hasMarkup,
      allowIndividualMarkups: state.markupConfig.allowIndividualMarkups,
      markupType: state.markupConfig.orderMarkupType,
      markupValue: state.markupConfig.orderMarkupValue,
      discountEnabled: state.discountConfig.enabled,
      allowIndividualDiscounts: state.discountConfig.allowIndividualDiscounts,
      discountType: state.discountConfig.orderDiscountType,
      discountValue: state.discountConfig.orderDiscountValue,
    };
  }, [flowPhase, pricingDraft, state.discountConfig, state.markupConfig, state.selectedProducts]);

  return {
    state,
    orderSummary,
    flowPhase,
    configEditable,
    productsEditable,
    productsSectionActive,
    hasCommercialConfig,
    chargesSectionVisible,
    chargesEditable,
    paymentConfigVisible,
    paymentConfigEditable,
    paymentMethodsVisible,
    paymentMethodsEditable,
    paymentsSectionVisible,
    paymentsEditable,
    paymentsLocked,
    commitPricingConfig,
    confirmProductsSection,
    confirmChargesSection,
    confirmPaymentConfigSection,
    unlockConfigForEdit,
    unlockProductsForEdit,
    unlockChargesForEdit,
    unlockPaymentConfigForEdit,
    unlockPaymentMethodsForEdit,
    canConfirmConfigSection,
    canConfirmProductsSection,
    hasInvalidLinePricing,
    pricingDraft: committedConfigDraft,
    maxUniformDiscountAmount,
    orderMarkupHasNoCatalogBase,
    canProceed,
    paymentMethodOptions: PAYMENT_METHOD_OPTIONS,
    otherPaymentMethods,
    showOtherMethodsDialog,
    showPaymentAmountDialog,
    pendingPaymentMethod,
    editingPaymentId,
    payments: state.paymentConfig.payments,
    orderCharges: state.orderCharges,
    chargeCategories,
    addOrderCharge,
    updateOrderCharge,
    removeOrderCharge,
    handleMarkupToggle,
    handleIndividualMarkupToggle,
    handleMarkupTypeChange,
    handleMarkupValueChange,
    handleProductMarkupChange,
    handleDiscountToggle,
    handleIndividualDiscountToggle,
    handleDiscountTypeChange,
    handleDiscountValueChange,
    handleProductDiscountChange,
    handlePaymentMethodClick,
    handleOtherMethodSelect,
    handleOpenOtherMethods,
    handleCloseOtherMethods,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePaymentAmount,
    handleEditPayment,
    handleClosePaymentAmountDialog,
    handlePartialPaymentToggle,
    handleMultiplePaymentsToggle,
    handleNextStep,
    previousStep,
    formatAdjustmentDisplay,
    getPaymentMethodName,
    getTotalPayments,
    getRemainingBalance,
  };
};
